import { state } from '../state.js';
import { $ } from '../utils/dom.js';
import { fmtMoney, escapeHtml, truncate } from '../utils/format.js';
import { montoDisplay } from '../utils/currency.js';
import { getActiveSheet, ALL_SHEETS } from '../sheetsStore.js';
import {
  getAdminSession,
  getMetaEventDetail,
  loginAdmin,
  retryMetaEvent,
  updateSaleReview,
} from '../api/metaSales.js';

// Nombre del producto (hoja) de una venta: viene en la fila en modo "Todas
// las hojas"; con una hoja específica seleccionada es la hoja activa. Con la
// hoja por defecto ('') no conocemos el nombre.
function productOf(r) {
  if (r.SHEET_NAME) return r.SHEET_NAME;
  const active = getActiveSheet();
  return (active && active !== ALL_SHEETS) ? active : '';
}

function statusPill(s) {
  const k = (s || '').toLowerCase();
  let cls = 'pill';
  if (k.includes('realiz') || k.includes('complet') || k.includes('pag')) cls += ' ok';
  else if (k.includes('pend')) cls += ' warn';
  else if (k.includes('cancel') || k.includes('rech') || k.includes('fall')) cls += ' bad';
  return `<span class="${cls}">${escapeHtml(s || '—')}</span>`;
}

function metaPill(status, mode) {
  if (!status) return '<span class="meta">Solo Sheet</span>';
  const labels = {
    sent: 'Meta enviado',
    pending: 'Meta pendiente',
    processing: 'Procesando',
    failed: 'Meta fallido',
    blocked: 'Bloqueado',
    blocked_configuration: 'Falta configuración',
    dead_letter: 'Dead letter',
  };
  const kind = status === 'sent'
    ? 'ok'
    : ['pending', 'processing'].includes(status)
      ? 'warn'
      : 'bad';
  return `<span class="pill ${kind}">${escapeHtml(labels[status] || status)}</span>${
    mode === 'test' ? ' <span class="pill test">Modo de prueba</span>' : ''
  }`;
}

function formatDateTime(value) {
  if (!value) return '<span class="meta">—</span>';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '<span class="meta">—</span>'
    : date.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

function shortEventId(value) {
  if (!value) return '—';
  return value.length > 22 ? `${value.slice(0, 10)}…${value.slice(-8)}` : value;
}

export function renderDetail() {
  const rows = [...state.filtered];
  const { col, dir } = state.sort;
  const m = dir === 'asc' ? 1 : -1;
  rows.sort((a, b) => {
    const va = a[col], vb = b[col];
    if (col === 'FECHA') return ((va ? va.getTime() : 0) - (vb ? vb.getTime() : 0)) * m;
    if (col === 'MONTO') return (montoDisplay(a) - montoDisplay(b)) * m;
    if (col === 'SHEET_NAME') return productOf(a).localeCompare(productOf(b)) * m;
    return String(va || '').localeCompare(String(vb || '')) * m;
  });

  const totalPages = Math.max(1, Math.ceil(rows.length / state.pageSize));
  if (state.page > totalPages) state.page = totalPages;
  const slice = rows.slice((state.page - 1) * state.pageSize, state.page * state.pageSize);

  const t = $('detailTable');
  if (!rows.length) {
    t.innerHTML = `<tbody><tr><td class="empty">Sin resultados con estos filtros</td></tr></tbody>`;
    $('pager').innerHTML = '';
    return;
  }

  const headers = [
    { key: 'FECHA', label: 'Fecha' },
    { key: 'STATUS', label: 'Status' },
    { key: 'NOMBRE', label: 'Nombre' },
    { key: 'EMAIL', label: 'Email' },
    { key: 'MONTO', label: 'Monto', align: 'num' },
    { key: 'BANCO', label: 'Banco' },
    { key: 'AD_ID', label: 'AD ID' },
    { key: 'POST_ID', label: 'Post ID' },
    { key: 'NUMERO', label: '#' },
    { key: 'SHEET_NAME', label: 'Producto' },
    { key: 'SOURCE', label: 'Origen' },
    { key: 'EVENT_ID', label: 'event_id' },
    { key: 'CTWA_CLID', label: 'ctwa_clid' },
    { key: 'META_STATUS', label: 'Estado Meta' },
    { key: 'META_ATTEMPTS', label: 'Intentos', align: 'num' },
    { key: 'META_LAST_ATTEMPT', label: 'Último intento' },
    { key: 'META_SENT_AT', label: 'Enviado' },
    { key: 'META_ERROR_MESSAGE', label: 'Error' },
    { key: 'META_ACTIONS', label: 'Acciones' },
  ];

  const svgUp = `<svg class="arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle"><polyline points="18 15 12 9 6 15"/></svg>`;
  const svgDown = `<svg class="arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle"><polyline points="6 9 12 15 18 9"/></svg>`;
  const svgBoth = `<svg class="arrow" width="10" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;opacity:.5"><polyline points="7 15 12 20 17 15"/><polyline points="7 9 12 4 17 9"/></svg>`;
  const arrow = (k) => state.sort.col === k ? (state.sort.dir === 'asc' ? svgUp : svgDown) : svgBoth;

  t.innerHTML = `
    <thead><tr>${headers.map((h) => `<th data-col="${h.key}" class="${h.align || ''}">${h.label}${arrow(h.key)}</th>`).join('')}</tr></thead>
    <tbody>${slice.map((r) => `
      <tr>
        <td>${r.FECHA ? r.FECHA.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : escapeHtml(r.FECHA_RAW)}</td>
        <td>${statusPill(r.STATUS)}</td>
        <td title="${escapeHtml(r.NOMBRE)}">${escapeHtml(r.NOMBRE)}</td>
        <td title="${escapeHtml(r.EMAIL)}">${escapeHtml(r.EMAIL)}</td>
        <td class="num">${r.MONTO ? fmtMoney(montoDisplay(r)) : '<span class="meta">—</span>'}</td>
        <td title="${escapeHtml(r.BANCO)}">${escapeHtml(truncate(r.BANCO, 40))}</td>
        <td>${escapeHtml(r.AD_ID)}</td>
        <td>${escapeHtml(r.POST_ID)}</td>
        <td>${escapeHtml(r.NUMERO)}</td>
        <td>${productOf(r) ? escapeHtml(productOf(r)) : '<span class="meta">—</span>'}</td>
        <td>${escapeHtml(r.SOURCE || 'google_sheets')}</td>
        <td title="${escapeHtml(r.EVENT_ID || '')}">${escapeHtml(shortEventId(r.EVENT_ID))}</td>
        <td class="raw-value">${
          r.CTWA_CLID
            ? escapeHtml(r.CTWA_CLID)
            : r.CTWA_PRESENT === true
              ? '<span class="pill ok">Sí</span>'
              : r.CTWA_PRESENT === false
                ? '<span class="pill bad">No</span>'
                : '<span class="meta">—</span>'
        }</td>
        <td>${metaPill(r.META_STATUS, r.META_MODE)}</td>
        <td class="num">${r.META_ATTEMPTS ?? '—'}</td>
        <td>${formatDateTime(r.META_LAST_ATTEMPT)}</td>
        <td>${formatDateTime(r.META_SENT_AT)}</td>
        <td title="${escapeHtml(r.META_ERROR_MESSAGE || '')}">${escapeHtml(truncate(r.META_ERROR_MESSAGE || '', 55)) || '<span class="meta">—</span>'}</td>
        <td>${r.DELIVERY_ID ? `
          <div class="meta-actions">
            <button class="meta-detail-btn" data-delivery="${escapeHtml(r.DELIVERY_ID)}">Ver</button>
            ${r.META_STATUS !== 'sent' ? `<button class="meta-retry-btn" data-delivery="${escapeHtml(r.DELIVERY_ID)}">Reenviar</button>` : ''}
          </div>` : '<span class="meta">—</span>'}</td>
      </tr>
    `).join('')}</tbody>`;

  t.querySelectorAll('th').forEach((th) => {
    th.onclick = () => {
      const c = th.dataset.col;
      if (state.sort.col === c) state.sort.dir = state.sort.dir === 'asc' ? 'desc' : 'asc';
      else {
        state.sort.col = c;
        state.sort.dir = (c === 'FECHA' || c === 'MONTO') ? 'desc' : 'asc';
      }
      renderDetail();
    };
  });

  t.querySelectorAll('.meta-detail-btn').forEach((button) => {
    button.onclick = () => openMetaDetail(button.dataset.delivery);
  });
  t.querySelectorAll('.meta-retry-btn').forEach((button) => {
    button.onclick = () => retryDelivery(button, button.dataset.delivery);
  });

  const svgL = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle"><polyline points="15 18 9 12 15 6"/></svg>`;
  const svgR = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle"><polyline points="9 18 15 12 9 6"/></svg>`;
  $('pager').innerHTML = `
    <span>Mostrando ${(state.page - 1) * state.pageSize + 1}–${Math.min(state.page * state.pageSize, rows.length)} de ${rows.length}</span>
    <span>
      <button ${state.page === 1 ? 'disabled' : ''} id="prevPg">${svgL} Anterior</button>
      Página ${state.page} / ${totalPages}
      <button ${state.page === totalPages ? 'disabled' : ''} id="nextPg">Siguiente ${svgR}</button>
    </span>`;
  const prev = $('prevPg'), next = $('nextPg');
  if (prev) prev.onclick = () => { state.page--; renderDetail(); };
  if (next) next.onclick = () => { state.page++; renderDetail(); };
}

async function ensureAdmin() {
  const session = await getAdminSession();
  if (session.authenticated) return true;
  if (!session.configured) {
    alert('La sesión administrativa aún no está configurada en Vercel.');
    return false;
  }
  const password = window.prompt('Contraseña administrativa');
  if (!password) return false;
  await loginAdmin(password);
  return true;
}

async function retryDelivery(button, deliveryId) {
  button.disabled = true;
  const original = button.textContent;
  button.textContent = 'Enviando…';
  try {
    if (!(await ensureAdmin())) return;
    await retryMetaEvent(deliveryId);
    window.dispatchEvent(new Event('clicchat:meta-updated'));
  } catch (error) {
    alert(`No se pudo reenviar: ${error.message}`);
  } finally {
    button.disabled = false;
    button.textContent = original;
  }
}

async function openMetaDetail(deliveryId) {
  try {
    const detail = await getMetaEventDetail(deliveryId);
    const productOptions = (detail.products || []).map((product) =>
      `<option value="${escapeHtml(product.code)}" ${product.code === detail.sale.productCode ? 'selected' : ''}>${escapeHtml(product.name)} · ${escapeHtml(product.currency)}</option>`
    ).join('');
    const root = $('metaDetailRoot');
    root.innerHTML = `
      <div class="modal-backdrop" id="metaDetailBackdrop">
        <div class="modal meta-detail-modal">
          <div class="modal-head">
            <h2>Detalle de entrega Meta</h2>
            <button class="modal-close" id="metaDetailClose">×</button>
          </div>
          <div class="modal-body">
            <div class="modal-section">
              <h3>Resumen</h3>
              <pre>${escapeHtml(JSON.stringify({
                delivery: detail.delivery,
                sale: detail.sale,
                rawPayloadRetained: detail.rawPayloadRetained,
                receivedAt: detail.receivedAt,
                contentType: detail.contentType,
              }, null, 2))}</pre>
            </div>
            <div class="modal-section">
              <h3>Payload normalizado</h3>
              <pre>${escapeHtml(JSON.stringify(detail.normalizedPayload, null, 2))}</pre>
            </div>
            <div class="modal-section">
              <h3>Payload original</h3>
              <pre>${escapeHtml(JSON.stringify(detail.rawPayload, null, 2))}</pre>
            </div>
            <div class="modal-section">
              <h3>Respuesta segura de Meta</h3>
              <pre>${escapeHtml(JSON.stringify(detail.delivery.metaResponse, null, 2))}</pre>
            </div>
            <div class="modal-section">
              <h3>Corregir venta normalizada</h3>
              <p class="modal-help">Guardar conserva el mismo event_id y vuelve a dejar la entrega pendiente.</p>
              <div class="review-grid">
                <label>Producto
                  <select id="reviewProduct"><option value="">Selecciona…</option>${productOptions}</select>
                </label>
                <label>Monto
                  <input id="reviewAmount" inputmode="decimal" value="${escapeHtml(detail.sale.amount || '')}" />
                </label>
                <label>Moneda ISO
                  <input id="reviewCurrency" maxlength="3" value="${escapeHtml(detail.sale.currency || '')}" />
                </label>
                <label>ctwa_clid
                  <input id="reviewCtwa" value="${escapeHtml(detail.sale.ctwaClid || '')}" autocomplete="off" />
                </label>
              </div>
              <button class="btn secondary" id="saveSaleReview">Guardar corrección</button>
              <span class="modal-help">Solo guardar cambios requiere la contraseña administrativa.</span>
              <span class="modal-status" id="saleReviewStatus"></span>
            </div>
          </div>
        </div>
      </div>`;
    const close = () => { root.innerHTML = ''; };
    $('metaDetailClose').onclick = close;
    $('metaDetailBackdrop').onclick = (event) => {
      if (event.target.id === 'metaDetailBackdrop') close();
    };
    $('saveSaleReview').onclick = async () => {
      const button = $('saveSaleReview');
      const status = $('saleReviewStatus');
      button.disabled = true;
      status.textContent = 'Guardando…';
      try {
        if (!(await ensureAdmin())) {
          status.textContent = 'No se realizaron cambios.';
          button.disabled = false;
          return;
        }
        await updateSaleReview(detail.sale.id, {
          productCode: $('reviewProduct').value || undefined,
          amount: $('reviewAmount').value || undefined,
          currency: $('reviewCurrency').value.trim().toUpperCase() || undefined,
          ctwaClid: $('reviewCtwa').value.trim() || null,
        });
        status.textContent = 'Guardado. La entrega quedó pendiente.';
        status.className = 'modal-status ok';
        window.dispatchEvent(new Event('clicchat:meta-updated'));
      } catch (error) {
        status.textContent = `Error: ${error.message}`;
        status.className = 'modal-status bad';
        button.disabled = false;
      }
    };
  } catch (error) {
    alert(`No se pudo abrir el detalle: ${error.message}`);
  }
}
